import React, { Component } from 'react'
import { PaymentQueueStatus, PaymentQueueOrderType } from '@getgreenline/homi-shared'
import { ParkedSaleStore } from './ParkedSaleStore'
import { observer, inject } from 'mobx-react'
import { RefreshMessage } from './RefreshMessage'
import { CancelAllModal } from './CancelAllModal'
import { DateFilter } from '../../DashboardReports/Financial/ReportFilter/DateFilter'
import moment from 'moment'
import { RangePickerPresetRange } from 'antd/lib/date-picker/interface'
import { message, Select, Radio, Input, Icon } from 'antd'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { capitalizeFirstLetter, parseErrorMsg, GrRegex } from '../../../../utilities/helpers'
import { ExternalSourceAPI, ExternalSourceModels } from '@getgreenline/external-sources'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { debounce } from 'lodash'
import { BLAZE_COMPANY } from '../../../../constants/general'
import { BlazeSelectFilter } from '../../../../components/CustomSelect/BlazeSelectFilter'
import { FilterDropdownV2 } from '../../../../components/FilterDropdown/FilterDropdownV2'

interface Props {
  parkedSaleStore: ParkedSaleStore
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface StatusOptions {
  value: PaymentQueueStatus
  text: string
}

interface State {
  showCancelAllModal: boolean
  externalSources: ExternalSourceModels.ICompanyExternalSourceContract[]
  searchQuery?: string
}

@inject('currentCompanyStore')
@observer
export class FilterGroup extends Component<Props & InjectedProps, State> {
  dateRangeLimitInMonths = 3

  constructor(props: Props & InjectedProps) {
    super(props)

    this.state = {
      showCancelAllModal: false,
      externalSources: [],
    }
  }

  componentDidMount = async () => {
    this.getExternalSources()
    this.addNewStatus()
  }

  statusOptions: StatusOptions[] = [
    {
      value: PaymentQueueStatus.READY_FOR_PICKUP,
      text: 'Ready for pickup',
    },
    {
      value: PaymentQueueStatus.OUT_FOR_DELIVERY,
      text: 'Out for delivery',
    },
    {
      value: PaymentQueueStatus.PENDING,
      text: 'Order placed',
    },
    {
      value: PaymentQueueStatus.CANCELLED,
      text: 'Cancelled',
    },
    {
      value: PaymentQueueStatus.COMPLETED,
      text: 'Completed',
    },
  ]

  addNewStatus = () => {
    // Add to status filter
    this.statusOptions.splice(1, 0, {
      value: PaymentQueueStatus.READY_FOR_DELIVERY,
      text: 'Ready for delivery',
    })

    // Pre-select the option
    const { defaultFilter } = this.props.parkedSaleStore
    if (defaultFilter.status.length < 4) {
      defaultFilter.status.push(PaymentQueueStatus.READY_FOR_DELIVERY)
    }
  }

  resetLimitAndSearch = () => {
    const { filter, searchPaymentQueues } = this.props.parkedSaleStore
    filter.setOffset(0)
    searchPaymentQueues()
  }

  debounceResetLimitAndSearch = debounce(() => {
    const { filter, searchPaymentQueues } = this.props.parkedSaleStore
    filter.setOffset(0)
    searchPaymentQueues()
  }, 400)

  resetFilter = () => {
    const { filter, defaultFilter } = this.props.parkedSaleStore

    filter.setSearchQuery('')
    filter.setStatus(defaultFilter.status)
    filter.setLimit(defaultFilter.limit)
    filter.setLocationIds(defaultFilter.locationIds)
    filter.setExternalSourceId(undefined)
    filter.setOrderType(undefined)
    this.props.parkedSaleStore.setStartDate(defaultFilter.startDate)
    this.props.parkedSaleStore.setEndDate(defaultFilter.endDate)
    this.resetLimitAndSearch()
  }

  setLocationIds = (locationIds: number[]) => {
    const { filter } = this.props.parkedSaleStore

    filter.setLocationIds(locationIds)
    this.resetLimitAndSearch()
  }

  toggleSelectedLocationId = (locationId: number) => {
    const { filter } = this.props.parkedSaleStore

    filter.toggleLocationId(locationId)
    this.resetLimitAndSearch()
  }

  setStatus = (status: string[]) => {
    const { filter } = this.props.parkedSaleStore

    filter.setStatus(status)
    this.resetLimitAndSearch()
  }

  setOrderType = (orderType: PaymentQueueOrderType) => {
    const { filter } = this.props.parkedSaleStore

    filter.setOrderType(orderType)
    this.resetLimitAndSearch()
  }

  setExternalSourceId = (source: number | string) => {
    const { filter } = this.props.parkedSaleStore

    filter.setExternalSourceId(source)
    this.resetLimitAndSearch()
  }

  setSearchQuery = (searchQuery: string) => {
    const { filter } = this.props.parkedSaleStore
    const query = searchQuery.replace(GrRegex.unicodeRegex, '')

    this.setState({ searchQuery: query })
    filter.setSearchQuery(query)
    this.debounceResetLimitAndSearch()
  }

  handleStartDateChange = (date: Date | null) => {
    if (date) {
      this.props.parkedSaleStore.setStartDate(date)
    }
  }

  handleEndDateChange = (date: Date | null) => {
    if (date) {
      this.props.parkedSaleStore.setEndDate(date)
    }
    if (this.props.parkedSaleStore.isDateLimitExceeded) {
      message.error('Range exceeded 3 month limit')
    } else {
      this.resetLimitAndSearch()
    }
  }

  getExternalSources = async () => {
    try {
      const externalSources = await ExternalSourceAPI.getCompanyExternalSources(
        this.props.currentCompanyStore!.company!.id,
      )
      this.setState({ externalSources })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'Failed to get external sources')
    }
  }

  get presets(): { [range: string]: RangePickerPresetRange } {
    return {
      Today: [moment().startOf('day'), moment().endOf('day')],
      Yesterday: [
        moment().subtract(1, 'day').startOf('day'),
        moment().subtract(1, 'day').endOf('day'),
      ],
      '2 days ago': [
        moment().subtract(2, 'day').startOf('day'),
        moment().subtract(2, 'day').endOf('day'),
      ],
      'This week': [moment().startOf('week'), moment().endOf('week')],
      'Last week': [
        moment().subtract(1, 'week').startOf('week'),
        moment().subtract(1, 'week').endOf('week'),
      ],
      '2 weeks ago': [
        moment().subtract(2, 'week').startOf('week'),
        moment().subtract(2, 'week').endOf('week'),
      ],
      'This month': [moment().startOf('month'), moment().endOf('month')],
      'Last month': [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
      '2 months ago': [
        moment().subtract(2, 'month').startOf('month'),
        moment().subtract(2, 'month').endOf('month'),
      ],
      '3 months ago': [
        moment().subtract(3, 'month').startOf('month'),
        moment().subtract(3, 'month').endOf('month'),
      ],
    }
  }

  get allLocationsSelected() {
    const { locations, filteredLocations } = this.props.parkedSaleStore
    return filteredLocations.length === locations.length
  }

  get showAllStatuses() {
    const { filteredStatus } = this.props.parkedSaleStore
    return filteredStatus.length === this.statusOptions.length
  }

  get locationsDropdownTitle() {
    const { filteredLocations } = this.props.parkedSaleStore
    return this.allLocationsSelected ? 'All' : `${filteredLocations.length}`
  }

  get statusDropdownTitle() {
    const { filteredStatus } = this.props.parkedSaleStore
    return filteredStatus.length === this.statusOptions.length ? 'All' : `${filteredStatus.length}`
  }

  get availableSources(): Array<{ value: number | string | null; label: string }> {
    const { externalSources } = this.state
    const availableForClickAndCollect = ExternalSourceModels.EXTERNAL_SOURCES_CLICK_AND_COLLECT.map(
      (e) => e.toLowerCase(),
    )

    const betaStrippedExternalSources = externalSources.map((externalSource) => {
      const BETA_SUFFIX = ' (BETA)'
      if (externalSource.name.toUpperCase().endsWith(BETA_SUFFIX)) {
        externalSource.name = externalSource.name.replace(BETA_SUFFIX, '')
      }
      return externalSource
    })

    const availableExternalSources = betaStrippedExternalSources
      .filter((externalSource) =>
        availableForClickAndCollect.includes(externalSource.name.toLowerCase()),
      ) // Filter to only allowed
      .map((externalSource) => ({
        value: externalSource.id || null,
        label: externalSource.name,
      }))
    const result = [{ value: 'Greenline', label: BLAZE_COMPANY }, ...availableExternalSources]
    return result.sort((a, b) => a.label.localeCompare(b.label))
  }

  render() {
    const { parkedSaleStore } = this.props
    const { company, filter, locations } = parkedSaleStore
    const allLocationIds = locations.map(({ id }) => id)

    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 8 }}>
          <div className='input-group' style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <Input
                className='gr-filter-item gr-filter-gutter'
                prefix={<Icon type='search' />}
                placeholder='ID, Customer'
                value={filter.searchQuery}
                onChange={(e) => this.setSearchQuery(e.target.value)}
                style={{ width: 140 }}
              />

              <span className='mr-2'>
                <DateFilter
                  startDate={parkedSaleStore.startDate}
                  endDate={parkedSaleStore.endDate}
                  onStartDateChange={this.handleStartDateChange}
                  onEndDateChange={this.handleEndDateChange}
                  presets={this.presets}
                  allowClear={false}
                  wrapperStyle={{ width: 220, display: 'inline-block' }}
                  maxRangeMonths={12}
                />
              </span>

              <FilterDropdownV2
                label='Locations'
                options={locations}
                selectedOptionIds={filter.locationIds}
                onSelect={this.toggleSelectedLocationId}
                selectAllOptions={() => {
                  this.setLocationIds(allLocationIds)
                }}
                unselectAllOptions={() => {
                  this.setLocationIds([])
                }}
              />

              <BlazeSelectFilter<number | string>
                className='gr-filter-item gr-filter-gutter'
                allowClear
                showSearch
                showArrow
                placeholder='Source'
                dropdownMatchSelectWidth={false}
                value={filter.externalSourceId}
                style={{ width: 100 }}
                onChange={(source) => {
                  this.setExternalSourceId(source)
                }}
              >
                {this.availableSources.map((source, index) => (
                  <Select.Option key={index} value={source.value || source.label}>
                    {source.label}
                  </Select.Option>
                ))}
              </BlazeSelectFilter>

              <Select<PaymentQueueOrderType>
                className='gr-filter-item gr-filter-gutter'
                allowClear
                showSearch
                showArrow={true}
                placeholder='Order Type'
                value={filter.orderType}
                dropdownMatchSelectWidth={false}
                style={{ width: 120 }}
                onChange={(orderType) => {
                  this.setOrderType(orderType)
                }}
              >
                {Object.values(PaymentQueueOrderType).map((orderType) => (
                  <Select.Option key={orderType}>{capitalizeFirstLetter(orderType)}</Select.Option>
                ))}
              </Select>

              <Select<string[]>
                className='gr-filter-item gr-filter-gutter'
                allowClear
                showArrow={true}
                placeholder='Status'
                dropdownMatchSelectWidth={false}
                maxTagCount={0}
                maxTagTextLength={5}
                maxTagPlaceholder={`Status (${filter.status.length})`}
                value={filter.status}
                mode='multiple'
                onChange={(status) => {
                  this.setStatus(status)
                }}
              >
                {this.statusOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.text}
                  </Select.Option>
                ))}
              </Select>

              <Select<number>
                className='gr-filter-item gr-filter-gutter'
                value={filter.limit}
                optionLabelProp='label'
                dropdownMatchSelectWidth={false}
                style={{ width: 120 }}
                onChange={(value) => {
                  filter.setLimit(value)
                  this.resetLimitAndSearch()
                }}
              >
                <Select.Option key={20} value={20} label={'20 per page'}>
                  <Radio checked={filter.limit === 20}>20 per page</Radio>
                </Select.Option>
                <Select.Option key={30} value={30} label={'30 per page'}>
                  <Radio checked={filter.limit === 30}>30 per page</Radio>
                </Select.Option>
                <Select.Option key={40} value={40} label={'40 per page'}>
                  <Radio checked={filter.limit === 40}>40 per page</Radio>
                </Select.Option>
                <Select.Option key={50} value={50} label={'50 per page'}>
                  <Radio checked={filter.limit === 50}>50 per page</Radio>
                </Select.Option>
              </Select>

              <BlazeButton
                buttonProps={{
                  type: 'primary',
                  onClick: this.resetLimitAndSearch,
                  disabled: parkedSaleStore.loading,
                }}
                titleProps={{ title: 'Apply' }}
              />

              <BlazeButton
                buttonProps={{
                  className: 'sgr-btn-ghost',
                  type: 'link',
                  onClick: () => this.resetFilter(),
                }}
                titleProps={{ title: 'Reset' }}
              />
            </div>

            <div>
              <BlazeButton
                buttonProps={{
                  type: 'default',
                  onClick: () => this.setState({ showCancelAllModal: true }),
                  icon: 'close',
                }}
                titleProps={{ title: 'Clear all' }}
              />

              {this.state.showCancelAllModal && (
                <CancelAllModal
                  companyId={company!.id}
                  onClose={() => this.setState({ showCancelAllModal: false })}
                  onSubmit={() => {
                    this.resetLimitAndSearch()
                    this.setState({ showCancelAllModal: false })
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 8 }}>
          <div className='input-group'>
            <div className='mr-2 text-muted' style={{ alignSelf: 'center' }}>
              <RefreshMessage parkedSaleStore={parkedSaleStore} />
            </div>
          </div>
        </div>
      </>
    )
  }
}
